import React, { useState } from 'react';
import { 
  Calendar,
  BookOpen,
  Clock,
  Target,
  FileText,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  Lightbulb,
  Download,
  Printer,
  AlertCircle,
  Coffee,
  Award
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";

// Year 13 ICT - Yearly Planner Data
const yearlyPlannerData = {
  subject: "Year 13 ICT",
  year: 2025,
  totalWeeks: 40,
  terms: [
    {
      id: "term1",
      name: "Term 1",
      weeks: 13,
      dateRange: "January 27 - April 25",
      focus: "ICT Trends & Information Systems",
      strands: ["Strand 1"],
      color: "blue",
      schedule: [
        { week: 1, date: "Jan 27 - Jan 31", topic: "Course Introduction & Syllabus Brief", unit: "-", type: "Admin", strand: "-" },
        { week: 2, date: "Feb 3 - Feb 7", topic: "Future ICT Trends - Identifying Key Features", unit: "1.1", type: "Lesson", strand: "1", sessions: 1 },
        { week: 3, date: "Feb 10 - Feb 14", topic: "Future ICT Trends - Outlining Multiple Features", unit: "1.1", type: "Lesson", strand: "1", sessions: 2 },
        { week: 4, date: "Feb 17 - Feb 21", topic: "Understanding Workflows", unit: "1.2", type: "Lesson", strand: "1", sessions: 1 },
        { week: 5, date: "Feb 24 - Feb 28", topic: "Workflows & Information Systems Connection", unit: "1.2", type: "Lesson", strand: "1", sessions: 2 },
        { week: 6, date: "Mar 3 - Mar 7", topic: "IS Applications and Benefits", unit: "1.2", type: "Lesson", strand: "1", sessions: 3 },
        { week: 7, date: "Mar 10 - Mar 14", topic: "IS Development & Implementation", unit: "1.2", type: "Lesson", strand: "1", sessions: 4 },
        { week: 8, date: "Mar 17 - Mar 21", topic: "Student Information Systems Case Study", unit: "1.2", type: "Lesson", strand: "1", sessions: 5 },
        { week: 9, date: "Mar 24 - Mar 28", topic: "Tourism Management Systems", unit: "1.2", type: "Lesson", strand: "1", sessions: 6 },
        { week: 10, date: "Mar 31 - Apr 4", topic: "ICT Infrastructure Fundamentals", unit: "1.3", type: "Lesson", strand: "1", sessions: 1 },
        { week: 11, date: "Apr 7 - Apr 11", topic: "Networking & Implementation Concerns", unit: "1.3", type: "Lesson", strand: "1", sessions: 2 },
        { week: 12, date: "Apr 14 - Apr 18", topic: "Network Types & Features", unit: "1.3", type: "Lesson", strand: "1", sessions: 3 },
        { week: 13, date: "Apr 21 - Apr 25", topic: "Term 1 Assessment - Strand 1", unit: "1.1-1.3", type: "Assessment", strand: "1", assessmentType: "Test" },
        { week: "Break", date: "Apr 28 - May 2", topic: "Term 1 Holiday (1 Week)", unit: "-", type: "Holiday", strand: "-" }
      ],
      assessments: [
        { title: "Unit 1.1 Quiz - ICT Trends", marks: 15, type: "Quiz", week: 3 },
        { title: "Unit 1.2 Project - IS Case Study", marks: 25, type: "Project", week: 9 },
        { title: "Term 1 Test - Strand 1", marks: 40, type: "Test", week: 13 }
      ],
      totalMarks: 80
    },
    {
      id: "term2",
      name: "Term 2",
      weeks: 12,
      dateRange: "May 5 - July 25",
      focus: "Digital Media & Web Development",
      strands: ["Strand 2"],
      color: "green",
      schedule: [
        { week: 1, date: "May 5 - May 9", topic: "Introduction to Digital Media", unit: "2.1", type: "Lesson", strand: "2", sessions: 1 },
        { week: 2, date: "May 12 - May 16", topic: "Digital Media Devices & Equipment", unit: "2.1", type: "Lesson", strand: "2", sessions: 2 },
        { week: 3, date: "May 19 - May 23", topic: "Content Creation Requirements", unit: "2.1", type: "Lesson", strand: "2", sessions: 3 },
        { week: 4, date: "May 26 - May 30", topic: "Software Tools for Digital Media", unit: "2.1", type: "Lesson", strand: "2", sessions: 4 },
        { week: 5, date: "Jun 2 - Jun 6", topic: "Basic Media Modification", unit: "2.1", type: "Lesson", strand: "2", sessions: 5 },
        { week: 6, date: "Jun 9 - Jun 13", topic: "Advanced Modification & Integration", unit: "2.1", type: "Lesson", strand: "2", sessions: 6-7 },
        { week: 7, date: "Jun 16 - Jun 20", topic: "Web Design Requirements", unit: "2.2", type: "Lesson", strand: "2", sessions: 1 },
        { week: 8, date: "Jun 23 - Jun 27", topic: "CSS Introduction & Types", unit: "2.2", type: "Lesson", strand: "2", sessions: 2 },
        { week: 9, date: "Jun 30 - Jul 4", topic: "CSS Syntax & Declarations", unit: "2.2", type: "Lesson", strand: "2", sessions: 3 },
        { week: 10, date: "Jul 7 - Jul 11", topic: "CSS Advantages & HTML Linking", unit: "2.2", type: "Lesson", strand: "2", sessions: 4 },
        { week: 11, date: "Jul 14 - Jul 18", topic: "Website Development Project", unit: "2.2", type: "Lesson", strand: "2", sessions: 5-8 },
        { week: 12, date: "Jul 21 - Jul 25", topic: "Term 2 Assessment - Strand 2", unit: "2.1-2.2", type: "Assessment", strand: "2", assessmentType: "Test + Project" },
        { week: "Break", date: "Jul 28 - Aug 15", topic: "Term 2 Holiday (3 Weeks)", unit: "-", type: "Holiday", strand: "-" }
      ],
      assessments: [
        { title: "Unit 2.1 Practical - Digital Media Creation", marks: 30, type: "Practical", week: 6 },
        { title: "Unit 2.2 Project - Website Development", marks: 40, type: "Project", week: 11 },
        { title: "Term 2 Test - Strand 2", marks: 30, type: "Test", week: 12 }
      ],
      totalMarks: 100
    },
    {
      id: "term3",
      name: "Term 3",
      weeks: 10,
      dateRange: "August 18 - October 24",
      focus: "Programming & Problem Solving",
      strands: ["Strand 3"],
      color: "purple",
      schedule: [
        { week: 1, date: "Aug 18 - Aug 22", topic: "Introduction to Problem Solving", unit: "3.1", type: "Lesson", strand: "3", sessions: 1 },
        { week: 2, date: "Aug 25 - Aug 29", topic: "Problem-Solving Steps & Techniques", unit: "3.1", type: "Lesson", strand: "3", sessions: 2 },
        { week: 3, date: "Sep 1 - Sep 5", topic: "Design Methodologies (Top-Down/Bottom-Up)", unit: "3.2", type: "Lesson", strand: "3", sessions: 1-2 },
        { week: 4, date: "Sep 8 - Sep 12", topic: "Representing Solutions - Pseudocode", unit: "3.2", type: "Lesson", strand: "3", sessions: 3 },
        { week: 5, date: "Sep 15 - Sep 19", topic: "Representing Solutions - Flowcharts", unit: "3.2", type: "Lesson", strand: "3", sessions: 4 },
        { week: 6, date: "Sep 22 - Sep 26", topic: "Variables, Data Types & Operators", unit: "3.2", type: "Lesson", strand: "3", sessions: 5 },
        { week: 7, date: "Sep 29 - Oct 3", topic: "Modular Programming & Functions", unit: "3.2", type: "Lesson", strand: "3", sessions: 6 },
        { week: 8, date: "Oct 6 - Oct 10", topic: "Programming Practice & Coding", unit: "3.2", type: "Lesson", strand: "3", sessions: "Practice" },
        { week: 9, date: "Oct 13 - Oct 17", topic: "Programming Project Work", unit: "3.1-3.2", type: "Lesson", strand: "3", sessions: "Project" },
        { week: 10, date: "Oct 20 - Oct 24", topic: "Term 3 Assessment - Strand 3", unit: "3.1-3.2", type: "Assessment", strand: "3", assessmentType: "Test + Project" },
        { week: "Break", date: "Oct 27 - Oct 31", topic: "Term 3 Holiday (1 Week)", unit: "-", type: "Holiday", strand: "-" }
      ],
      assessments: [
        { title: "Unit 3.1 Quiz - Problem Solving", marks: 15, type: "Quiz", week: 2 },
        { title: "Unit 3.2 Practical - Pseudocode & Flowcharts", marks: 25, type: "Practical", week: 5 },
        { title: "Programming Project", marks: 40, type: "Project", week: 9 },
        { title: "Term 3 Test - Strand 3", marks: 30, type: "Test", week: 10 }
      ],
      totalMarks: 110
    },
    {
      id: "term4",
      name: "Term 4",
      weeks: 8,
      dateRange: "November 3 - December 12",
      focus: "Microprocessors, Ethics & Final Exams",
      strands: ["Strand 4"],
      color: "red",
      schedule: [
        { week: 1, date: "Nov 3 - Nov 7", topic: "Microprocessor Architecture & Components", unit: "4.1", type: "Lesson", strand: "4", sessions: 1 },
        { week: 2, date: "Nov 10 - Nov 14", topic: "Code Translation (Compilers, Interpreters)", unit: "4.1", type: "Lesson", strand: "4", sessions: 2 },
        { week: 3, date: "Nov 17 - Nov 21", topic: "Interfacing - Sensors & Actuators", unit: "4.1", type: "Lesson", strand: "4", sessions: 3-4 },
        { week: 4, date: "Nov 24 - Nov 28", topic: "Embedded Systems & Applications", unit: "4.1", type: "Lesson", strand: "4", sessions: 5-6 },
        { week: 5, date: "Dec 1 - Dec 5", topic: "ICT Ethics & Social Issues", unit: "4.2", type: "Lesson", strand: "4", sessions: 1 },
        { week: 6, date: "Dec 8 - Dec 12", topic: "Revision & Exam Preparation", unit: "All", type: "Revision", strand: "All" },
        { week: 7, date: "Dec 15 - Dec 19", topic: "Final Examinations", unit: "All", type: "Assessment", strand: "All", assessmentType: "Final Exam" },
        { week: 8, date: "Dec 22 onwards", topic: "Course Completion & Results", unit: "-", type: "Admin", strand: "-" }
      ],
      assessments: [
        { title: "Unit 4.1 Quiz - Microprocessors", marks: 20, type: "Quiz", week: 4 },
        { title: "Unit 4.2 Case Study - Ethics", marks: 20, type: "Project", week: 5 },
        { title: "Final Examination", marks: 130, type: "Exam", week: 7 }
      ],
      totalMarks: 170
    }
  ]
};

// Get type badge color
const getTypeBadge = (type) => {
  const colors = {
    'Lesson': 'bg-blue-100 text-blue-800',
    'Assessment': 'bg-red-100 text-red-800',
    'Admin': 'bg-gray-100 text-gray-800',
    'Holiday': 'bg-green-100 text-green-800',
    'Revision': 'bg-purple-100 text-purple-800'
  };
  return colors[type] || 'bg-gray-100 text-gray-800';
};

// Get strand badge color
const getStrandBadge = (strand) => {
  const colors = {
    '1': 'bg-blue-600',
    '2': 'bg-green-600',
    '3': 'bg-purple-600',
    '4': 'bg-red-600',
    'All': 'bg-gray-600',
    '-': 'bg-gray-300'
  };
  return colors[strand] || 'bg-gray-400';
};

// Get term color
const getTermColor = (color) => {
  const colors = {
    'blue': { bg: 'bg-blue-500', gradient: 'from-blue-500 to-blue-600', light: 'bg-blue-50' },
    'green': { bg: 'bg-green-500', gradient: 'from-green-500 to-green-600', light: 'bg-green-50' },
    'purple': { bg: 'bg-purple-500', gradient: 'from-purple-500 to-purple-600', light: 'bg-purple-50' },
    'red': { bg: 'bg-red-500', gradient: 'from-red-500 to-red-600', light: 'bg-red-50' }
  };
  return colors[color] || colors['blue'];
};

export default function EmbeddedYearlyPlanner() {
  const [selectedTerm, setSelectedTerm] = useState('term1');
  const [expandedWeek, setExpandedWeek] = useState(null);
  
  const planner = yearlyPlannerData;
  const currentTerm = planner.terms.find(t => t.id === selectedTerm);
  
  // Calculate total marks for the year
  const totalYearMarks = planner.terms.reduce((acc, term) => acc + term.totalMarks, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Calendar className="h-6 w-6" />
                {planner.subject} - Yearly Planner {planner.year}
              </CardTitle>
              <CardDescription className="text-indigo-100 mt-1">
                {planner.totalWeeks} teaching weeks • 4 terms • {totalYearMarks} total marks
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm">
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button variant="secondary" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Term Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {planner.terms.map((term) => {
          const termColor = getTermColor(term.color);
          return (
            <Card 
              key={term.id} 
              className={`cursor-pointer transition-all hover:shadow-lg ${selectedTerm === term.id ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setSelectedTerm(term.id)}
            >
              <CardHeader className={`pb-2 bg-gradient-to-r ${termColor.gradient} text-white rounded-t-lg`}>
                <CardTitle className="text-lg">{term.name}</CardTitle>
                <CardDescription className="text-white/80 text-xs">
                  {term.dateRange}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-3">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Weeks:</span>
                    <span className="font-medium">{term.weeks}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Marks:</span>
                    <span className="font-medium">{term.totalMarks}</span>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {term.strands.map((s, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {s}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{term.focus}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Term Tabs */}
      <Tabs value={selectedTerm} onValueChange={setSelectedTerm}>
        <TabsList className="grid w-full grid-cols-4">
          {planner.terms.map((term) => (
            <TabsTrigger key={term.id} value={term.id}>
              {term.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {planner.terms.map((term) => {
          const termColor = getTermColor(term.color);
          return (
            <TabsContent key={term.id} value={term.id} className="space-y-4 mt-4">
              {/* Term Header */}
              <Card className={termColor.light}>
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <Badge className={`${termColor.bg} mb-2`}>{term.name}</Badge>
                      <CardTitle className="text-xl">{term.focus}</CardTitle>
                      <CardDescription>
                        {term.dateRange} • {term.weeks} weeks • {term.totalMarks} marks
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-primary">{term.assessments.length}</p>
                        <p className="text-xs text-muted-foreground">Assessments</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-primary">{term.totalMarks}</p>
                        <p className="text-xs text-muted-foreground">Marks</p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Weekly Schedule Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5" />
                    Weekly Schedule
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Week</TableHead>
                        <TableHead className="w-32">Date</TableHead>
                        <TableHead>Topic</TableHead>
                        <TableHead className="w-20">Unit</TableHead>
                        <TableHead className="w-24">Strand</TableHead>
                        <TableHead className="w-28">Type</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {term.schedule.map((item, index) => (
                        <TableRow 
                          key={index}
                          className={`${item.type === 'Holiday' ? 'bg-green-50' : ''} ${item.type === 'Assessment' ? 'bg-red-50' : ''}`}
                        >
                          <TableCell className="font-medium">
                            {item.week === 'Break' ? (
                              <Badge variant="outline" className="bg-green-100">
                                <Coffee className="h-3 w-3 mr-1" />
                                Break
                              </Badge>
                            ) : (
                              <Badge variant="outline">{item.week}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {item.date}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {item.type === 'Assessment' && <Award className="h-4 w-4 text-red-500" />}
                              {item.type === 'Holiday' && <Coffee className="h-4 w-4 text-green-500" />}
                              <span className={item.type === 'Assessment' ? 'font-medium text-red-700' : ''}>
                                {item.topic}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {item.unit !== '-' ? (
                              <Badge variant="outline" className="font-mono">
                                {item.unit}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {item.strand !== '-' ? (
                              <Badge className={getStrandBadge(item.strand)}>
                                Strand {item.strand}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={getTypeBadge(item.type)}>
                              {item.type}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Assessments Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    {term.name} Assessments
                  </CardTitle>
                  <CardDescription>
                    Total: {term.totalMarks} marks
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {term.assessments.map((assessment, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full ${termColor.bg} flex items-center justify-center text-white font-bold`}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium">{assessment.title}</p>
                            <p className="text-sm text-muted-foreground">
                              Week {assessment.week} • {assessment.type}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-primary">{assessment.marks}</p>
                          <p className="text-xs text-muted-foreground">marks</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Progress bar */}
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Term contribution to yearly total:</span>
                      <span className="font-medium">{Math.round((term.totalMarks / totalYearMarks) * 100)}%</span>
                    </div>
                    <Progress value={(term.totalMarks / totalYearMarks) * 100} className="h-2" />
                  </div>
                </CardContent>
              </Card>

              {/* Learning Outcomes */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="h-5 w-5" />
                    {term.name} Learning Focus
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {term.strands.map((strand, i) => {
                      const strandNum = strand.replace('Strand ', '');
                      const outcomes = getStrandOutcomes(strandNum);
                      return (
                        <div key={i} className={`p-4 rounded-lg ${termColor.light}`}>
                          <Badge className={getStrandBadge(strandNum)} >
                            {strand}
                          </Badge>
                          <h4 className="font-semibold mt-2 mb-2">{outcomes.title}</h4>
                          <ul className="space-y-1">
                            {outcomes.items.map((item, j) => (
                              <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                                <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Year Summary */}
      <Card className="bg-gradient-to-r from-gray-50 to-slate-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Year Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="text-3xl font-bold text-blue-600">4</p>
              <p className="text-sm text-muted-foreground">Terms</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="text-3xl font-bold text-green-600">{planner.totalWeeks}</p>
              <p className="text-sm text-muted-foreground">Weeks</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="text-3xl font-bold text-purple-600">4</p>
              <p className="text-sm text-muted-foreground">Strands</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="text-3xl font-bold text-red-600">{planner.terms.reduce((acc, t) => acc + t.assessments.length, 0)}</p>
              <p className="text-sm text-muted-foreground">Assessments</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="text-3xl font-bold text-orange-600">{totalYearMarks}</p>
              <p className="text-sm text-muted-foreground">Total Marks</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Helper function to get strand outcomes
function getStrandOutcomes(strandNum) {
  const outcomes = {
    '1': {
      title: 'ICT Trends & Information Systems',
      items: [
        'Identify future ICT trends in education, health, and home',
        'Understand workflows and information systems',
        'Explain IS development and implementation',
        'Understand ICT infrastructure and networking'
      ]
    },
    '2': {
      title: 'Digital Media & Web Development',
      items: [
        'Understand digital media importance and types',
        'Use software tools for media creation',
        'Apply CSS concepts and syntax',
        'Develop functional websites'
      ]
    },
    '3': {
      title: 'Programming & Problem Solving',
      items: [
        'Apply problem-solving techniques',
        'Use design methodologies',
        'Create pseudocode and flowcharts',
        'Understand programming concepts and coding'
      ]
    },
    '4': {
      title: 'Microprocessors & Ethics',
      items: [
        'Understand microprocessor architecture',
        'Explain embedded systems applications',
        'Analyze ethical issues in ICT',
        'Apply ethical decision-making frameworks'
      ]
    }
  };
  return outcomes[strandNum] || outcomes['1'];
}
